import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import moment from 'moment';
import { AiFillHeart, AiOutlineHeart } from 'react-icons/ai';
import {
  OrderingSelectors,
  OrderingConstants,
  OrderingOperations,
  OrderingHooks,
} from 'polygon-ordering';
import { formatPoints } from 'polygon-utils';
import getThemeLookup from '../selectors/getThemeLookup';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import getExtremelyShortScreen from '../selectors/getExtremelyShortScreen';
import getAdditionalDeliveryStatusesEnabled from '../selectors/getAdditionalDeliveryStatusesEnabled';
import getHideTimes from '../selectors/getHideTimes';
import { setCurrentModal } from '../thunks/setCurrentModal';
import combineStyles from '../utils/combineStyles';
import { INITIAL_SCREEN_ROUTE } from '../hooks/useRoutes';
import ScreenHero from '../components/ScreenHero';
import ScreenFloating from '../components/ScreenFloating';
import NewOrderButton from '../components/NewOrderButton';
import Text from '../components/Text';
import OrderStatus from '../components/OrderStatus';
import PoweredByDoordashDriveBadge from '../components/PoweredByDoordashDriveBadge';
import PoweredByUberDirectBadge from '../components/PoweredByUberDirectBadge';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { useTranslation } from 'react-i18next';
import StandardButton from '../components/StandardButton';
import ViewOrderDetails from '../components/ViewOrderDetails';
import { FAVOURITE_NAME_MODAL } from '../modals/FavouriteNameModal';
import RadioCheck from '../components/RadioCheck';
import { REGISTER_MODAL_ID } from '../modals/RegisterModal';
import { TEXT_PROPERTIES } from '../utils/theme';
import { DeliveryAddressDto } from '../libs/polygon-ordering/src/dtos/DeliveryAddressDto';
import getRegisterProfileFields from '../selectors/getRegisterProfileFields';
import { useRedcatAnalytics, trackSignupStarted } from '../analytics';

const {
  getSaleDetails,
  getSaleType,
  getTable,
  getDeliveryProvider,
  getMember,
  getLocation,
  getFavouriteOrders,
  getStagedPurchasesAsShadowItems,
  getOrderTotals,
  getSingleOrderDetails,
  getOrderInProgress,
} = OrderingSelectors;

const {
  SALE_STATUSES_MAP,
  READY_TO_COLLECT,
  PROCESSED_AT_STORE,
  DELIVERED,
  DELIVERY_PROVIDERS,
  ORDER_SUBMITTED,
  DELIVERY_SALE_TYPES,
  SALE_TYPE,
} = OrderingConstants;
const { fetchSaleStatus, setSaleStatus, deleteFavouriteOrder } = OrderingOperations;

const { useFormattedCurrency } = OrderingHooks;

const getShouldShowCheckMark = ({
  saleType,
  rawStatus,
  trackingUrl,
}: {
  saleType: SALE_TYPE;
  rawStatus: Sale['status'];
  trackingUrl?: Sale['deliveryTrackingUrl'];
}) => {
  const status = SALE_STATUSES_MAP[rawStatus]?.toString();

  if (saleType === SALE_TYPE.CATERING_DELIVERY) {
    return false;
  }

  // Managed delivery should only show the check mark for Order Submitted
  if (saleType === SALE_TYPE.DELIVERY && !trackingUrl && status === READY_TO_COLLECT) {
    return false;
  }
  // For delivery partners, pickup and takeaway the check mark should only show for the first and last step
  if (status && [ORDER_SUBMITTED, READY_TO_COLLECT, DELIVERED].includes(status)) {
    return true;
  }
  // Sale types that don't have status steps should always show the check mark
  if (
    [SALE_TYPE.CATERING, SALE_TYPE.DINE_IN, SALE_TYPE.TABLE_ORDER, SALE_TYPE.WEB_ORDERING].includes(
      saleType,
    )
  ) {
    return true;
  }
  return false;
};

const CompletedOrderScreen = () => {
  // TODO: Get some confirmation on the comment below
  const FETCH_STATUS_INTERVAL_DURATION = 10000; // should this be configurable?

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const p = useAppSelector(getThemeLookup);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const saleDetails = useAppSelector(getSaleDetails);
  const saleType = useAppSelector(getSaleType);
  const table = useAppSelector(getTable);
  const deliveryProvider = useAppSelector(getDeliveryProvider);
  const member = useAppSelector(getMember);
  const additionalDeliveryStatusesEnabled = useAppSelector(getAdditionalDeliveryStatusesEnabled);
  const extremelyShortScreen = useAppSelector(getExtremelyShortScreen);
  const hideTimes = useAppSelector(getHideTimes);
  const registerProfileFields = useAppSelector(getRegisterProfileFields);
  const { track } = useRedcatAnalytics();
  const tableNumberLabel = useAppSelector(state => state.config.tableNumberLabel);
  const location = useAppSelector(getLocation);
  const deliveryAddress = useAppSelector(state => state.desiredDeliveryAddress);
  const favouriteOrders = useAppSelector(getFavouriteOrders);
  const orderTotals = useAppSelector(getOrderTotals);

  const deliveryAddressString = DeliveryAddressDto.toFullAddressString(deliveryAddress);

  const deliveryByUber = saleDetails?.deliveryTrackingUrl?.includes('delivery.uber.com');

  const loyaltyMemberCardsetId = useAppSelector(state => state.config.loyaltyMemberCardset);
  const cardsets = useAppSelector(state => state.ordering.config.cardsets);
  const loyaltyMemberCardset =
    (loyaltyMemberCardsetId &&
      Object.values(cardsets || {}).find(
        cardset => cardset.alphaPrefix === loyaltyMemberCardsetId,
      )) ||
    undefined;

  const _orderTotals =
    (loyaltyMemberCardset &&
      loyaltyMemberCardset.accumulatePoints &&
      loyaltyMemberCardset.pointsAwardRate &&
      orderTotals?.moneyPrice &&
      // add in the rewards award as if the user was logged in
      !orderTotals.rewardsAward && {
        ...orderTotals,
        rewardsAward: Math.round(orderTotals?.moneyPrice / loyaltyMemberCardset?.pointsAwardRate),
      }) ||
    orderTotals;

  const showPointsPotential = !!_orderTotals?.pointsAward || !!_orderTotals?.rewardsAward;
  const rewardsCurrencyString = useFormattedCurrency({
    cents: _orderTotals?.rewardsAward,
    hideSymbol: true,
  });
  const pointsEarnedString =
    (!!_orderTotals?.rewardsAward && rewardsCurrencyString) ||
    formatPoints(_orderTotals?.pointsAward ?? 0);
  const loyaltyPointsName = useAppSelector(state => state.config.loyaltyPointsName);
  const shadowPurchases = useAppSelector(getStagedPurchasesAsShadowItems);

  const fetchStatusInterval = useRef<NodeJS.Timeout | null>(null);
  const readyInterval = useRef<NodeJS.Timeout | null>(null);

  const [viewOrderDetails, setViewOrderDetails] = useState(false);

  const guestOrderingOrderDetails = useAppSelector(getSingleOrderDetails);

  const orderInProgress = useAppSelector(getOrderInProgress);
  const orderDetails = orderInProgress || saleDetails;

  const fetchStatus = () => {
    if (!saleDetails) {
      return;
    }

    if (saleType === SALE_TYPE.CATERING && saleDetails.items?.length !== 0) {
      return;
    }

    if (
      saleDetails.status <
        SALE_STATUSES_MAP[additionalDeliveryStatusesEnabled ? DELIVERED : PROCESSED_AT_STORE] ||
      saleType === SALE_TYPE.CATERING
    ) {
      dispatch(fetchSaleStatus({ authenticationMethod: member ? 'member' : 'none' }));
    } else {
      clearInterval(fetchStatusInterval.current as NodeJS.Timeout);

      if (
        !additionalDeliveryStatusesEnabled &&
        saleDetails.status !== SALE_STATUSES_MAP[READY_TO_COLLECT]
      ) {
        readyInterval.current = setInterval(checkIfReady, 1000);
      }
    }
  };

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    fetchStatusInterval.current = setInterval(fetchStatus, FETCH_STATUS_INTERVAL_DURATION);
    fetchStatus();

    setTimeout(() => {
      setIsMounted(true);
    }, 350);

    return () => {
      clearInterval(fetchStatusInterval.current as NodeJS.Timeout);
      clearInterval(readyInterval.current as NodeJS.Timeout);
    };
  }, []);

  const checkIfReady = () => {
    if (saleDetails!.pickupTime && moment(saleDetails!.pickupTime) <= moment()) {
      //@ts-ignore
      dispatch(setSaleStatus(SALE_STATUSES_MAP[READY_TO_COLLECT]));
      clearInterval(readyInterval.current as NodeJS.Timeout);
    }
  };

  const isDelivery = DELIVERY_SALE_TYPES.includes(saleType);

  const isPickupOrTakeAway =
    saleType === SALE_TYPE.PICKUP ||
    saleType === SALE_TYPE.TAKE_AWAY ||
    saleType === SALE_TYPE.WEB_ORDERING ||
    saleType === SALE_TYPE.CATERING ||
    saleType === SALE_TYPE.APP_LANE_PICKUP;
  const isDineIn = saleType === SALE_TYPE.DINE_IN || saleType === SALE_TYPE.TABLE_ORDER;

  const ScreenComponent = deviceTypeMobile || extremelyShortScreen ? ScreenHero : ScreenFloating;

  const completedOrderMessage = t(
    saleDetails?.status &&
      [
        SALE_TYPE.DELIVERY,
        SALE_TYPE.PICKUP,
        SALE_TYPE.TAKE_AWAY,
        SALE_TYPE.APP_LANE_PICKUP,
      ].includes(saleType)
      ? `completedOrderMessage.${saleType}.${SALE_STATUSES_MAP[saleDetails.status]}`
      : `completedOrderMessage.${saleType}`,
  );

  const historicalOrderItems =
    orderDetails && (member ? shadowPurchases : guestOrderingOrderDetails?.items);

  const tempFavouriteOrders = useRef(favouriteOrders || []);
  const newFavouritedOrder = useRef(favouriteOrders ? favouriteOrders[0] : null);

  const isOrderFavourited = useMemo(() => {
    let favouritesIncreased =
      favouriteOrders && favouriteOrders?.length > tempFavouriteOrders?.current.length;
    if (favouritesIncreased && favouriteOrders) {
      newFavouritedOrder.current = favouriteOrders[0];
    }

    return favouritesIncreased;
  }, [favouriteOrders]);

  if (!saleDetails) {
    return <Redirect to={INITIAL_SCREEN_ROUTE} />;
  }
  if (viewOrderDetails) {
    return <ViewOrderDetails setViewOrderDetails={setViewOrderDetails} />;
  }

  const getMainContainerStyle = () => {
    if (deviceTypeMobile) {
      return styles.mainContainerMobile;
    }

    if (deliveryByUber) {
      // Needed for Safari and Firefox
      return styles.crossBrowserContainer;
    }

    return styles.mainContainerDesktop;
  };

  const getDeviceBackgroundStyles = () => {
    if (deviceTypeMobile) {
      return p('completedOrderScreenMobile', ['backgroundColor']);
    }

    return p('completedOrderScreenDesktop', ['backgroundColor', 'boxShadow']);
  };

  if (Number(saleDetails.status) === 4 && saleDetails.deliveryTrackingUrl) {
    return (
      <React.Fragment>
        <ScreenComponent
          forceHideHero={true}
          parentContainerStyle={
            deviceTypeMobile ? styles.mobileContentContainer : styles.desktopContentContainer
          }
        >
          <div
            style={combineStyles(
              getMainContainerStyle(),
              p('screen', ['backgroundColor']),
              p('completedOrderScreen', ['backgroundColor']),
              getDeviceBackgroundStyles(),
            )}
          >
            <iframe
              // the 'ptr' param cleans up the UI for Uber. It does nothing for Doordash
              src={saleDetails.deliveryTrackingUrl + '?ptr=randomstring'}
              style={{ height: '100%' }}
              title="Delivery Tracker"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </ScreenComponent>
        <NewOrderButton />
      </React.Fragment>
    );
  }

  const orderReadyTime = moment(
    DELIVERY_SALE_TYPES.includes(saleType)
      ? saleDetails.estimatedDeliveryTime
      : saleDetails.pickupTime,
  );

  const isOrderReadyTimeFutureOrdering = !orderReadyTime.isSame(moment(), 'day');

  return (
    <>
      <ScreenComponent
        forceHideHero={true}
        parentContainerStyle={
          deviceTypeMobile ? styles.mobileContentContainer : styles.desktopContentContainer
        }
      >
        <div
          style={combineStyles(
            styles.mainContainer,
            !deviceTypeMobile ? styles.mainContainerDesktop : styles.mainContainerMobile,

            p('screen', ['backgroundColor']),
            p('completedOrderScreen', ['backgroundColor']),

            !deviceTypeMobile && p('completedOrderScreenDesktop', ['backgroundColor', 'boxShadow']),
            deviceTypeMobile && p('completedOrderScreenMobile', ['backgroundColor']),
          )}
        >
          <div style={styles.detailsSection}>
            {/* Only some sale types should show the status steps */}
            {![
              SALE_TYPE.CATERING,
              SALE_TYPE.DINE_IN,
              SALE_TYPE.TABLE_ORDER,
              SALE_TYPE.WEB_ORDERING,
            ].includes(saleType) &&
              !isOrderReadyTimeFutureOrdering && <OrderStatus />}

            {Boolean(completedOrderMessage) && (
              <div style={styles.completedOrderMessageContainer}>
                <Text themeKey="completedOrderMessage" style={styles.completedOrderMessage}>
                  {completedOrderMessage}
                </Text>
              </div>
            )}
            {isDelivery && Boolean(saleDetails.deliveryTrackingUrl) && (
              <>
                {deliveryProvider?.id === DELIVERY_PROVIDERS.DOORDASH_DRIVE && (
                  <PoweredByDoordashDriveBadge />
                )}

                {deliveryProvider?.id === DELIVERY_PROVIDERS.UBER_DIRECT && (
                  <PoweredByUberDirectBadge />
                )}
              </>
            )}

            {getShouldShowCheckMark({
              saleType,
              rawStatus: saleDetails.status,
              trackingUrl: saleDetails.deliveryTrackingUrl,
            }) ? (
              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <RadioCheck size={40} checked={isMounted}></RadioCheck>
              </div>
            ) : null}

            <div style={styles.orderCompleteDetails}>
              {!hideTimes &&
                !isDineIn &&
                saleDetails?.status !== (SALE_STATUSES_MAP[READY_TO_COLLECT] as number) && (
                  <div>
                    <Text themeKey="orderReadyTime">
                      {isDelivery && t('title.arrivalTime')}
                      {isPickupOrTakeAway && t('title.pickupTime')}
                      {`: `}
                    </Text>
                    <Text themeKey="orderReadyTime">
                      {orderReadyTime.format(
                        isOrderReadyTimeFutureOrdering ? 'Do MMMM YYYY h:mm A' : 'h:mm A',
                      )}
                    </Text>
                  </div>
                )}

              <div style={styles.numberAndLocation}>
                {Boolean(saleDetails.folioId) && (
                  <Text
                    themeKey="completedOrderHeading"
                    block
                    variant="h1"
                    style={combineStyles(p('completedOrderNumber', TEXT_PROPERTIES), {
                      marginTop: 10,
                      marginBottom: isDineIn ? 'initial' : 16,
                    })}
                  >
                    {t('title.orderNumber') + ` ${saleDetails.folioId}`}
                  </Text>
                )}
                {isPickupOrTakeAway && location && (
                  <Text themeKey="completedOrderWhereabouts" block>
                    {t('title.pickUpLocation') + ` ${location.name}`}
                  </Text>
                )}
                {isDelivery && deliveryAddress && (
                  <Text
                    themeKey="completedOrderWhereabouts"
                    style={styles.orderCompleteDetailsText}
                    block
                  >
                    {t('title.deliveringToAddress') + ` ${deliveryAddressString}`}
                  </Text>
                )}
                {saleType === SALE_TYPE.APP_LANE_PICKUP &&
                  Boolean(t('title.completedOrderSupportingText')) && (
                    <Text themeKey="completedOrderSupportingText" block>
                      {t('title.completedOrderSupportingText')}
                    </Text>
                  )}
              </div>

              {isDineIn && Boolean(table) && (
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Text style={styles.title} block>
                    {tableNumberLabel || t('tableNumber')}
                  </Text>
                  <Text
                    themeKey="completedOrderDetail"
                    style={combineStyles(styles.title, { marginLeft: 4 })}
                  >
                    {table}
                  </Text>
                </div>
              )}
            </div>

            <StandardButton
              themeKey="viewOrderDetailsButton"
              label={t('title.viewOrderDetails')}
              containerStyle={styles.icon}
              onClick={() => setViewOrderDetails(true)}
            />

            {!member && (
              <>
                <Text themeKey="joinLoyaltyTitle" style={styles.joinLoyaltyTitle} block>
                  {t('joinLoyaltyProgram')}
                </Text>

                {showPointsPotential && (
                  <Text
                    themeKey="joinLoyaltyCallMessage"
                    style={styles.joinLoyaltyCallMessage}
                    block
                  >
                    {t('joinLoyaltyCallMessage', {
                      pointsEarned: pointsEarnedString,
                      pointsName: (
                        loyaltyPointsName ??
                        t('loyaltyPointsNameFallback') ??
                        ''
                      ).toLocaleLowerCase(),
                    })}
                  </Text>
                )}

                <StandardButton
                  themeKey="joinNowLoyaltyButton"
                  label={t('button.joinNow')}
                  containerStyle={styles.joinNowLoyaltyButton}
                  onClick={() => {
                    trackSignupStarted(track, {
                      entryPoint: 'checkout_flow',
                      signupMethod: 'native',
                      configuredFields: (registerProfileFields?.visible ?? []).map(
                        field => field.id,
                      ),
                    });
                    dispatch(setCurrentModal(REGISTER_MODAL_ID));
                  }}
                />
              </>
            )}
          </div>

          {member && historicalOrderItems && (
            <StandardButton
              themeKey="addOrderToFavourites"
              containerStyle={styles.addOrderToFavouritesContainer}
              style={{ marginTop: 30 }}
              onClick={() => {
                if (!isOrderFavourited) {
                  dispatch(
                    setCurrentModal({
                      modalId: FAVOURITE_NAME_MODAL,
                      params: {
                        historicalOrderItems,
                      },
                    }),
                  );
                } else if (newFavouritedOrder) {
                  dispatch(deleteFavouriteOrder({ id: newFavouritedOrder.current?.id as string }));
                }
              }}
            >
              {isOrderFavourited ? (
                <AiFillHeart style={combineStyles(p('activeHeartIcon', ['color']))} fontSize={34} />
              ) : (
                <AiOutlineHeart fontSize={34} />
              )}

              <Text themeKey="addOrderToFavourites" style={styles.addOrderToFavouritesText}>
                {t('title.addOrderToFavourites')}
              </Text>
            </StandardButton>
          )}
        </div>
      </ScreenComponent>

      <NewOrderButton />
    </>
  );
};

const styles: Styles = {
  orderCompleteDetails: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    gap: 20,
  },

  numberAndLocation: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    gap: 10,
  },

  mobileContentContainer: {
    display: 'flex',
  },

  desktopContentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'stretch',
    padding: '50px 30px',
  },

  mainContainer: {
    borderRadius: 5,
    padding: '3em 30px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  mainContainerDesktop: {
    maxWidth: 660,
    width: '100%',
    height: '100%',
    overflowY: 'auto',
  },
  mainContainerMobile: {
    flexGrow: 1,
    padding: '2em 30px 0',
  },

  detailsSection: {
    // marginTop: 10,

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2em',
    // height: '80%',
  },

  title: {
    fontSize: 18,
  },

  icon: {
    margin: '15px 0 0 0',
  },

  joinLoyaltyTitle: {
    margin: '20px 0 0 0',
  },

  joinLoyaltyCallMessage: {
    margin: '12px 0 0 0',
    width: '85%',
    textAlign: 'center',
  },

  joinNowLoyaltyButton: {
    width: '50%',
    borderRadius: 5,
    padding: '6px 15px',
  },

  addOrderToFavouritesContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 20,
  },

  addOrderToFavouritesText: {
    margin: '0 0 0 8px',
  },

  location: {
    paddingTop: 0,
  },

  newOrderButtonMobile: {
    margin: '10px 0 30px 0',
  },

  cartContainer: {
    width: '90%',
  },

  deliveryTrackingLinkIcon: {
    marginRight: 10,
    marginLeft: 5,
  },

  completedOrderMessage: {
    marginTop: 15,
  },

  completedOrderMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 40,
  },

  crossBrowserContainer: { minWidth: '73%', maxWidth: 1000 },
};

export default CompletedOrderScreen;
